import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Loader2, FileText, AlertCircle } from "lucide-react"
import { useGetDatasourceFileContent } from "@/api/datasource-bills-api"
import { type DatasourceBill } from "@/api/datasource-bills-api"

interface BillFullTextDialogProps {
  bill: DatasourceBill | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function BillFullTextDialog({ bill, open, onOpenChange }: BillFullTextDialogProps) {
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [fullTextContent, setFullTextContent] = useState<string | null>(null)
  
  const getFileContentMutation = useGetDatasourceFileContent()

  // Get available versions with text extracted
  const availableVersions = bill?.versions.filter(v => v.text_extracted && v.text_s3_key) || []
  
  // Set default selected version when dialog opens
  useEffect(() => {
    if (open && availableVersions.length > 0 && !selectedVersion) {
      setSelectedVersion(availableVersions[0].version)
    }
  }, [open, availableVersions, selectedVersion])

  const handleVersionChange = (versionStr: string) => {
    const version = parseInt(versionStr)
    setSelectedVersion(version)
    setFullTextContent(null) // Clear previous content
  }

  const handleViewFullText = async () => {
    if (!bill || !selectedVersion) return

    const version = availableVersions.find(v => v.version === selectedVersion)
    if (!version?.text_s3_key) return

    try {
      const content = await getFileContentMutation.mutateAsync(version.text_s3_key)
      setFullTextContent(content)
    } catch (error) {
      console.error('Failed to fetch full text:', error)
    }
  }

  const handleClose = () => {
    setFullTextContent(null)
    setSelectedVersion(null)
    onOpenChange(false)
  }

  if (!bill) return null

  const selectedVersionData = availableVersions.find(v => v.version === selectedVersion)

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={`${fullTextContent ? 'max-w-6xl max-h-[90vh]' : 'max-w-4xl max-h-[80vh]'} flex flex-col`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Full Text: {bill.bill_number}
          </DialogTitle>
          <DialogDescription>
            {bill.title}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Version Selection */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="flex items-center gap-2">
                <label htmlFor="version-select" className="text-sm font-medium">
                  Version:
                </label>
                <Select
                  value={selectedVersion?.toString() || ""}
                  onValueChange={handleVersionChange}
                  disabled={availableVersions.length === 0}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableVersions.map((version) => (
                      <SelectItem key={version.version} value={version.version.toString()}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">Version {version.version}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(version.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                            {version.text_extracted && ' • Text Available'}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 sm:ml-auto">
                <Button
                  onClick={handleViewFullText}
                  disabled={!selectedVersion || getFileContentMutation.isPending}
                >
                  {getFileContentMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      View Full Text
                    </>
                  )}
                </Button>
                
                {fullTextContent && (
                  <Button
                    variant="outline"
                    onClick={() => setFullTextContent(null)}
                  >
                    Hide Text
                  </Button>
                )}
              </div>
            </div>

            {/* Version Details - Hidden when viewing full text */}
            {selectedVersionData && !fullTextContent && (
              <div className="p-4 bg-muted/30 rounded-lg border">
                <h4 className="text-sm font-semibold mb-3 text-muted-foreground">Version {selectedVersionData.version} Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                  <div className="space-y-1">
                    <div className="font-medium text-muted-foreground">Created At</div>
                    <div className="font-mono">
                      {new Date(selectedVersionData.created_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="font-medium text-muted-foreground">MD5 Hash</div>
                    <div className="font-mono text-xs break-all">
                      {selectedVersionData.md5_hash}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="font-medium text-muted-foreground">Text Extracted</div>
                    <div className="flex items-center gap-2">
                      <Badge variant={selectedVersionData.text_extracted ? "default" : "secondary"}>
                        {selectedVersionData.text_extracted ? "Yes" : "No"}
                      </Badge>
                    </div>
                  </div>

                  {selectedVersionData.text_s3_key && (
                    <div className="space-y-1">
                      <div className="font-medium text-muted-foreground">Text S3 Key</div>
                      <div className="font-mono text-xs break-all">
                        {selectedVersionData.text_s3_key}
                      </div>
                    </div>
                  )}

                  {selectedVersionData.textract_job_id && (
                    <div className="space-y-1">
                      <div className="font-medium text-muted-foreground">Textract Job ID</div>
                      <div className="font-mono text-xs break-all">
                        {selectedVersionData.textract_job_id}
                      </div>
                    </div>
                  )}

                  <div className="space-y-1">
                    <div className="font-medium text-muted-foreground">Version Number</div>
                    <div className="font-mono">
                      {selectedVersionData.version}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error State */}
          {getFileContentMutation.isError && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm text-destructive">
                Failed to load full text content. Please try again.
              </span>
            </div>
          )}

          {/* No Versions Available */}
          {availableVersions.length === 0 && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                No versions with extracted text are available for this bill.
              </span>
            </div>
          )}

          {/* Content Display */}
          {fullTextContent && (
            <div className="flex-1 min-h-0 border rounded-md flex flex-col">
              <div className="p-3 border-b bg-muted/50 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    Full Text Content - Version {selectedVersion}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {fullTextContent.length.toLocaleString()} characters
                  </Badge>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-4 bg-background">
                <div className="text-sm leading-relaxed whitespace-pre-wrap font-mono">
                  {fullTextContent}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}